<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          System exception log
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          System exception log list
        </h5>

        <Search
            class="mb-3"
            :filters="filters"
            :loading="isSearching"
            :levelDropdown="levelDropdown"
            @search="getSystemExceptionLogList"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getSystemExceptionLogList"/>

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click="sortBy('createdAt')">
                  <span>Created at</span>
                  <i :class="getIconClass('createdAt')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('level')">
                  <span>Level</span>
                  <i :class="getIconClass('level')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('exceptionClass')">
                  <span>Exception class</span>
                  <i :class="getIconClass('exceptionClass')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('title')">
                  <span>Title</span>
                  <i :class="getIconClass('title')"></i>
                </a>
              </th>
              <th>Message</th>
              <th>File</th>
              <th>
                <a href="#" @click="sortBy('notified')">
                  <span>Notified</span>
                  <i :class="getIconClass('notified')"></i>
                </a>
              </th>
              <th style="text-align: right;">Details</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(log) in systemExceptionLogList.data" :key="log.id">
              <td>{{ log?.createdAt }}</td>
              <td>{{ log?.level }}</td>
              <td>{{ log?.exceptionClass }}</td>
              <td>{{ log?.title }}</td>
              <td class="text-truncate" style="max-width: 250px;">{{ log?.message }}</td>
              <td>{{ log?.file }}<span v-if="log?.line">:{{ log?.line }}</span></td>
              <td>
                <div class="pl-3">
                  <div v-if="log?.notified"
                       class="status-pill green p-2 mr-2"
                       data-toggle="tooltip"
                       data-original-title=""
                       title="">
                  </div>
                  <div v-else
                       class="status-pill red p-2 mr-2"
                       data-toggle="tooltip"
                       data-original-title=""
                       title="">
                  </div>
                </div>
              </td>
              <td class="text-right">
                <button class="btn btn-primary" @click="showDetails(log)">
                  <i class="os-icon os-icon-info"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="systemExceptionLogList?.pagination?.total_pages > 1"
            :totalPages="systemExceptionLogList?.pagination?.total_pages"
            :currentPage="systemExceptionLogList?.pagination?.current_page"
            :totalItems="systemExceptionLogList?.pagination?.total_items"
            :perPage="systemExceptionLogList?.pagination?.items_per_page"
            @pagechanged="getSystemExceptionLogList"/>
      </div>
    </div>

    <Modal
        v-model="showModal"
        title="Exception details"
        :hideFooter="true"
        sizeModal="modal-lg">
      <div v-if="selectedLog">
        <p><strong>Title:</strong> {{ selectedLog?.title }}</p>
        <p><strong>Exception class:</strong> {{ selectedLog?.exceptionClass }}</p>
        <p><strong>Level:</strong> {{ selectedLog?.level }}</p>
        <p><strong>File:</strong> {{ selectedLog?.file }}<span v-if="selectedLog?.line">:{{ selectedLog?.line }}</span></p>
        <p><strong>Created at:</strong> {{ selectedLog?.createdAt }}</p>
        <p><strong>Message:</strong></p>
        <pre class="p-2 bg-light" style="white-space: pre-wrap;">{{ selectedLog?.message }}</pre>
        <p v-if="selectedLog?.context && Object.keys(selectedLog.context).length"><strong>Context:</strong></p>
        <pre v-if="selectedLog?.context && Object.keys(selectedLog.context).length"
             class="p-2 bg-light" style="white-space: pre-wrap;">{{ selectedLog?.context }}</pre>
        <p><strong>Trace:</strong></p>
        <pre class="p-2 bg-light" style="white-space: pre-wrap;">{{ selectedLog?.trace }}</pre>
      </div>
    </Modal>
  </PageTemplate>
</template>


<script setup lang="ts">
import systemMonitoringService from '@/api/SystemMonitoring';
import Pagination from '@/components/Pagination.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import PerPage from '@/components/PerPage.vue';
import {onMounted, ref} from 'vue';
import Search from "@/views/App/SystemMonitoring/SystemExceptionLog/Search.vue";
import {getSortIconClass} from '@/shared/functions';

export interface SystemExceptionLog {
  id: number;
  title: string | null;
  message: string | null;
  exceptionClass: string | null;
  file: string | null;
  line: number | null;
  trace: string | null;
  context: Record<string, any> | null;
  level: string | null;
  notified: boolean;
  createdAt: string;
}

interface PaginationData {
  total_pages: number;
  current_page: number;
  items_per_page: number;
}

interface SystemExceptionLogListResponse {
  data: SystemExceptionLog[];
  pagination: PaginationData;
}

interface Filters {
  level: string;
  exceptionClass: string;
  notified: string;
  dateFrom: string;
  dateTo: string;
  searchTerm: string;
  sortBy: string;
  orderBy: string;
}

interface DropdownOption {
  value: string;
  label: string;
}

const systemExceptionLogList = ref<SystemExceptionLogListResponse | any>([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const showModal = ref(false);
const selectedLog = ref<SystemExceptionLog | null>(null);

const filters = ref<Filters>({
  level: '',
  exceptionClass: '',
  notified: '',
  dateFrom: '',
  dateTo: '',
  searchTerm: '',
  sortBy: 'createdAt',
  orderBy: 'DESC',
});

const levelDropdown = ref<DropdownOption[]>([
  {value: 'error', label: 'Error'},
  {value: 'critical', label: 'Critical'},
]);

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getSystemExceptionLogList();
}

function showDetails(log: SystemExceptionLog) {
  selectedLog.value = log;
  showModal.value = true;
}

async function getSystemExceptionLogList(page = 1, perPage?: number, passedFilters?: Filters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = systemExceptionLogList.value?.pagination?.items_per_page;

  try {
    const {data} = await systemMonitoringService.getSystemExceptionLogs({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    systemExceptionLogList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

onMounted(async () => {
  await getSystemExceptionLogList()
  isLoaded.value = true;
});

</script>

<style scoped>

</style>
